import { useCallback, useEffect, useRef } from "react";

import { isSelectionLocked } from "@/components/tiptap/lock/utils";
import { pasteNodes } from "@/input/utils/editOperators";
import { resolvePastedComputedResults } from "@/models/computedNode/alignPastedWrapperRefIds";
import { canReferenceAtCursor } from "@/models/computedNode/utils";
import { cursorIsActive } from "@/models/CursorModel";
import { ComputedResultNodeModel } from "@/models/nodes/ComputedResultNodeModel";
import { copyNodePropsFromDoc } from "@/models/nodes/copyNodePropsFromDoc";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

import { commitPendingInput } from "./commitPendingInput";

import type { PhantomHint } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson } from "@corca/data";

export function canDuplicateAtCursor(doc: DocumentModel, node: NodeModel): boolean {
  if (!["number", "variable", "constant", "computedResult"].includes(node.type)) {
    return false;
  }
  if (isSelectionLocked(doc)) return false;

  const { cursor } = doc;
  if (!cursor.isActive) return true;

  // The caret is already against the node, so the copy would land where it sits.
  if (cursor.nodeBeforeCursor === node || cursor.nodeAfterCursor === node) return false;

  if (node.type === "computedResult") {
    return canReferenceAtCursor(doc, node);
  }
  return true;
}

/**
 * Insert a copy of `node` at the caret, leaving the caret right after the copy.
 */
export function duplicateNodeAtCursor(doc: DocumentModel, node: NodeModel): boolean {
  const { cursor } = doc;
  if (!canDuplicateAtCursor(doc, node)) return false;

  const nodeJson = node.toJSON();

  return doc.transact(() => {
    let inlineNode = cursorIsActive(cursor) ? cursor.node : undefined;
    let { index } = cursor;

    if (!inlineNode) {
      inlineNode = doc.create(InlineNodeModel);
      doc.editor.tiptap?.commands.insertInlineExpression(inlineNode);
      index = 0;
      cursor.moveTo(inlineNode, index);
    } else {
      const adjustIndex = commitPendingInput(doc);
      doc.editor.resetInput(true);
      doc.selection.resetSelection();

      index = adjustIndex(inlineNode, index);
    }

    doc.input.requestSmoothScroll();
    cursor.moveTo(inlineNode, index);

    const nodes = pasteNodes(doc, [nodeJson]);

    return nodes.length > 0;
  }, "command:duplicateNodeAtCursor");
}

function getDuplicateNodeHint(doc: DocumentModel, node: NodeModel): PhantomHint | null {
  if (!canDuplicateAtCursor(doc, node)) return null;

  const label = node.is(ComputedResultNodeModel)
    ? doc.computedNodes.getComputedRefLabel(node)
    : undefined;

  const children = resolvePastedComputedResults(node.toJSON(), doc, undefined)
    .map((json) => copyNodePropsFromDoc(doc, json))
    .map((json) =>
      json.type === "computedReference" && label
        ? { ...json, props: { ...json.props, label } }
        : json,
    );
  if (children.length === 0) return null;

  const json: NodeJson = { type: "inline", props: {}, children };

  return {
    type: "reference",
    label: "reference-at-cursor",
    getHint: () => Promise.resolve({ json, equals: false }),
  };
}

function clearDuplicateNodeHint(doc: DocumentModel): void {
  doc.editor.hints.setPhantomHints([]);
  doc.editor.highlightFocusedDependencies();
}

/**
 * Ties the hover preview of `node` to the insertion it promises: the ghost goes up on hover and
 * comes down as `duplicateNode` inserts the real copy, or as the node leaves the screen.
 *
 * Only a computed result previews, and only while a plain click inserts it — behind a modifier
 * the ghost would promise an insertion that hovering alone never leads to.
 */
export function useDuplicateNodeHint(doc: DocumentModel, node: NodeModel | undefined) {
  const hintShown = useRef(false);

  const insertsOnPlainClick =
    doc.computedNodes.refOnClickEnabled && !!node?.is(ComputedResultNodeModel);

  const hideHint = useCallback(() => {
    if (!hintShown.current) return;
    hintShown.current = false;
    clearDuplicateNodeHint(doc);
  }, [doc]);

  const showHint = useCallback(() => {
    // The preview would replace hints already on screen, and leaving would not bring them back.
    if (!insertsOnPlainClick || !node || doc.editor.hints.hasHints) return;
    const hint = getDuplicateNodeHint(doc, node);
    if (!hint) return;
    hintShown.current = true;
    doc.editor.hints.setPhantomHints([hint]);
  }, [doc, node, insertsOnPlainClick]);

  // Unmounting while hovered — the insertion rebuilds the line — fires no mouseleave.
  useEffect(() => hideHint, [hideHint]);

  const duplicateNode = useCallback(() => {
    if (!node) return false;
    hideHint();
    return duplicateNodeAtCursor(doc, node);
  }, [doc, hideHint, node]);

  return {
    duplicateNode,
    insertsOnPlainClick,
    hintProps: { onMouseEnter: showHint, onMouseLeave: hideHint },
  };
}
